import { useMemo } from 'react';
import { useFocusTrap } from './useFocusTrap';

interface UseOverlayA11yOptions {
  /** Whether the overlay is currently visible / open. */
  open: boolean;
  /** Optional id of the header element to pair via aria-labelledby. */
  labelId?: string | undefined;
}

interface UseOverlayA11yResult {
  /** Attach to the overlay's content container. */
  ref: ReturnType<typeof useFocusTrap<HTMLElement>>;
  /**
   * Spread on the overlay container — bundles role / aria-modal /
   * aria-labelledby. Web-only attributes; native ignores.
   */
  a11yProps: {
    role: 'dialog';
    'aria-modal': 'true';
    'aria-labelledby'?: string;
  };
}

/**
 * Single source of truth for the "blocking overlay" ARIA + focus-trap
 * combo per ADR-0077.
 *
 * Before: AdaptivePanel.SheetPresentation / ModalPresentation /
 * SidebarPresentation, BaseSheet, and ConfirmDialog each called
 * `useFocusTrap({ enabled: open })` then spread `role="dialog"`,
 * `aria-modal="true"`, `aria-labelledby={labelId}` inline. Five sites,
 * identical shape, two of them with subtly different formatting (the
 * sidebar uses a `{ ... as any }` cast for Animated.View).
 *
 * After: one hook, one call site per overlay.
 *
 *   const overlay = useOverlayA11y({ open, labelId });
 *   <View ref={overlay.ref} {...overlay.a11yProps}>…</View>
 */
export function useOverlayA11y({
  open,
  labelId,
}: UseOverlayA11yOptions): UseOverlayA11yResult {
  const ref = useFocusTrap<HTMLElement>({ enabled: open });
  const a11yProps = useMemo(
    () => ({
      role: 'dialog' as const,
      'aria-modal': 'true' as const,
      ...(labelId ? { 'aria-labelledby': labelId } : null),
    }),
    [labelId],
  );
  return { ref, a11yProps };
}
